import React from 'react'

interface KPICardProps {
  title: string
  value: string | number
  format?: (value: any) => string
  trend?: 'up' | 'down' | 'neutral'
  comparison?: string
  icon?: React.ReactNode
  color?: string
}

const TrendIcon: React.FC<{ direction: 'up' | 'down' | 'neutral' }> = ({ direction }) => {
  if (direction === 'up') {
    return (
      <svg className="w-3.5 h-3.5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
      </svg>
    )
  }
  if (direction === 'down') {
    return (
      <svg className="w-3.5 h-3.5 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181" />
      </svg>
    )
  }
  return <span className="text-[11px] text-txt-tertiary">—</span>
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  format,
  trend,
  comparison,
  icon,
  color = 'brand',
}) => {
  const displayValue = format ? format(value) : value

  const colorMap: Record<string, string> = {
    brand: 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400',
    success: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning',
    error: 'bg-error-light text-error',
    info: 'bg-info-light text-info',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  }

  return (
    <div className="p-4 rounded-xl bg-surface border border-border hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm text-txt-secondary">{title}</p>
        {icon && (
          <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${colorMap[color] || colorMap.brand}`}>
            {icon}
          </div>
        )}
      </div>

      <div className="text-2xl font-bold text-txt-primary mb-1">{displayValue}</div>

      {(trend || comparison) && (
        <div className="flex items-center gap-1.5">
          {trend && <TrendIcon direction={trend} />}
          {comparison && (
            <span className="text-xs text-txt-tertiary">{comparison}</span>
          )}
        </div>
      )}
    </div>
  )
}
